import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Pencil, UploadCloud, Loader2, X, Lock, Sparkles, Printer, Tag, FolderCog, Check } from "lucide-react";
import JsBarcode from "jsbarcode";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = { name: "", category: "Plantes", price: 0, stock: 0, description: "", image: "", active: true,
  product_type: "vegetal", planting_period: "", watering: "", sun_exposure: "", care_tips: "",
  sold_by: "unite", unit_label: "", bigbag_volume_m3: 0,
  promo_price: "", promo_start: "", promo_end: "",
  cost_price: 0, cost_lines: [], supplier: "", purchase_conditions: "", delivery_delay: "",
  density: 0, footprint_m2: 0, depot_id: "", stock_by_depot: {},
  sizes: [], flowering_period: "", fertilizer_type: "", fertilizer_dose_kg: 0, fertilizer_bag_kg: 0 };

function Barcode({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try { JsBarcode(ref.current, value, { format: "CODE128", width: 1.6, height: 50, fontSize: 12, margin: 6 }); } catch { /* noop */ }
    }
  }, [value]);
  return <svg ref={ref} />;
}

function printBarcode(sku, name) {
  const w = window.open("", "_blank", "width=400,height=300");
  w.document.write(`<html><head><title>${sku}</title><script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.3/dist/JsBarcode.all.min.js"><\/script></head><body style="text-align:center;font-family:Arial;padding:20px"><p style="font-size:13px;margin:0 0 4px">${name}</p><svg id="bc"></svg><script>JsBarcode("#bc","${sku}",{format:"CODE128",width:2,height:70,fontSize:14});setTimeout(()=>window.print(),300);<\/script></body></html>`);
  w.document.close();
}

export default function ProductsAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [cats, setCats] = useState([]);
  const [depots, setDepots] = useState([]);
  const [showCatModal, setShowCatModal] = useState(false);

  const loadCats = () => api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => api.get("/products", { params: { active_only: false } }).then((r) => setItems(r.data)));
  useEffect(() => { load(); loadCats(); api.get("/depots").then((r) => setDepots(r.data)).catch(() => {}); }, []);

  const aiCare = async () => {
    if (!form.name) { toast.error("Saisissez le nom du végétal"); return; }
    setAiBusy(true);
    try { const { data } = await api.post("/products/ai-care", { name: form.name, category: form.category });
      setForm((f) => ({ ...f, planting_period: data.planting_period || "", watering: data.watering || "", sun_exposure: data.sun_exposure || "", care_tips: data.care_tips || "" }));
      toast.success("Fiche de culture générée par l'IA");
    } catch { toast.error("Échec IA"); } finally { setAiBusy(false); }
  };

  const upload = async (file) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((f) => ({ ...f, image: data.path })); toast.success("Image téléversée"); }
    catch { toast.error("Échec"); } finally { setBusy(false); }
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Nom et prix requis"); return; }
    setSaving(true);
    try {
      const clines = (form.cost_lines || []).filter((l) => l.label || l.amount).map((l) => ({ label: l.label || "", amount: Number(l.amount || 0) }));
      const sbd = {};
      if (depots.length) depots.forEach((d) => { sbd[d.id] = Number((form.stock_by_depot || {})[d.id] || 0); });
      const totalStock = depots.length ? Object.values(sbd).reduce((a, b) => a + b, 0) : Number(form.stock);
      const payload = { ...form, price: Number(form.price), stock: totalStock, cost_price: Number(form.cost_price || 0),
        stock_by_depot: depots.length ? sbd : null,
        cost_lines: clines, density: Number(form.density || 0), footprint_m2: Number(form.footprint_m2 || 0),
        fertilizer_dose_kg: Number(form.fertilizer_dose_kg || 0), fertilizer_bag_kg: Number(form.fertilizer_bag_kg || 0),
        sizes: Array.isArray(form.sizes) ? form.sizes : String(form.sizes || "").split(",").map((s) => s.trim()).filter(Boolean),
        bigbag_volume_m3: Number(form.bigbag_volume_m3 || 0), promo_price: form.promo_price ? Number(form.promo_price) : null };
      if (editing) await api.put(`/products/${editing}`, payload); else await api.post("/products", payload);
      toast.success(editing ? "Produit mis à jour" : "Produit créé");
      setForm(empty); setEditing(null); load();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const edit = (p) => {
    const hasSbd = p.stock_by_depot && Object.keys(p.stock_by_depot).length;
    const sbd = hasSbd ? p.stock_by_depot : (depots[0] ? { [depots[0].id]: p.stock || 0 } : {});
    setEditing(p.id); setForm({ ...empty, ...p, stock_by_depot: sbd }); window.scrollTo(0, 0);
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/products/${id}`); load(); };

  const input = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";
  const costLines = form.cost_lines || [];
  const costTotal = costLines.length ? costLines.reduce((s, l) => s + Number(l.amount || 0), 0) : Number(form.cost_price || 0);
  const setLine = (i, k, v) => { const arr = [...costLines]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, cost_lines: arr }); };
  const addLine = () => setForm({ ...form, cost_lines: [...costLines, { label: "", amount: 0 }] });
  const delLine = (i) => setForm({ ...form, cost_lines: costLines.filter((_, k) => k !== i) });
  const margin = (Number(form.price) - costTotal);
  const marginPct = form.price ? Math.round((margin / Number(form.price)) * 100) : 0;

  return (
    <div>
      <h1 className="font-display text-4xl text-forest mb-1">Boutique — Produits</h1>
      <p className="text-stone mb-6">Gérez le catalogue de la pépinière</p>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        {/* Form */}
        <div className="rounded-xl bg-white border border-border p-6 h-fit" data-testid="product-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-forest">{editing ? "Modifier le produit" : "Nouveau produit"}</h3>
            {editing && <button onClick={() => { setForm(empty); setEditing(null); }} className="text-stone hover:text-forest"><X size={18} /></button>}
          </div>
          <div className="space-y-3">
            <input className={input} placeholder="Nom du produit *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="prod-category">
                  {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  {form.category && !cats.some((c) => c.name === form.category) && <option value={form.category}>{form.category}</option>}
                </select>
                <button type="button" onClick={() => setShowCatModal(true)} data-testid="manage-categories" className="mt-1 text-xs text-brand hover:underline flex items-center gap-1"><FolderCog size={12} /> Gérer les catégories</button>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Actif (en ligne)</label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-stone">Prix de vente (€) *</label><input type="number" step="0.01" className={input} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" /></div>
              {!depots.length && <div><label className="text-xs text-stone">Stock</label><input type="number" className={input} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="prod-stock" /></div>}
            </div>
            {depots.length > 0 && (
              <div className="rounded-lg border border-border bg-[#F0EFEA] p-3">
                <p className="text-xs font-bold text-forest mb-2">Stock par dépôt <span className="font-normal text-stone">(total : {depots.reduce((a, d) => a + Number((form.stock_by_depot || {})[d.id] || 0), 0)})</span></p>
                <div className="grid grid-cols-2 gap-3">
                  {depots.map((d) => (
                    <div key={d.id}>
                      <label className="text-xs text-stone">{d.name}</label>
                      <input type="number" className={input} value={(form.stock_by_depot || {})[d.id] ?? 0} onChange={(e) => setForm({ ...form, stock_by_depot: { ...(form.stock_by_depot || {}), [d.id]: e.target.value } })} data-testid={`prod-stock-${d.id}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <textarea rows={3} className={input} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div>
              <label className="text-xs text-stone">Type de produit</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[{ v: "vegetal", l: "Végétal" }, { v: "materiau", l: "Matériau" }, { v: "produit", l: "Autre" }].map((t) => (
                  <button key={t.v} type="button" onClick={() => setForm({ ...form, product_type: t.v })} data-testid={`ptype-${t.v}`} className={`rounded-lg border py-2 text-xs font-medium ${form.product_type === t.v ? "border-brand bg-brand/5 text-brand" : "border-border text-stone"}`}>{t.l}</button>
                ))}
              </div>
            </div>
            {form.image ? (
              <div className="relative"><img src={fileUrl(form.image)} alt="" className="w-full h-36 object-cover rounded-lg" /><button onClick={() => setForm({ ...form, image: "" })} className="absolute top-2 right-2 bg-white rounded-full p-1"><X size={14} /></button></div>
            ) : (
              <label className="flex flex-col items-center gap-1 rounded-xl border-2 border-dashed border-border bg-[#F0EFEA] py-5 cursor-pointer hover:border-brand" data-testid="prod-upload">
                {busy ? <Loader2 className="animate-spin text-brand" /> : <UploadCloud className="text-brand" />}<span className="text-sm text-stone">Téléverser une image</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
              </label>
            )}

            {/* Plant care (AI) */}
            {form.product_type === "vegetal" && (
              <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 space-y-3" data-testid="care-block">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-green-700">Fiche de culture</p>
                  <button type="button" onClick={aiCare} disabled={aiBusy} data-testid="ai-care-btn" className="rounded-full bg-brand text-cream text-xs font-semibold px-3 py-1.5 flex items-center gap-1 hover:bg-forest disabled:opacity-60">{aiBusy ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Générer par IA</button>
                </div>
                <input className={input} placeholder="Période de plantation" value={form.planting_period} onChange={(e) => setForm({ ...form, planting_period: e.target.value })} data-testid="care-planting" />
                <input className={input} placeholder="Période de floraison" value={form.flowering_period} onChange={(e) => setForm({ ...form, flowering_period: e.target.value })} data-testid="care-flowering" />
                <input className={input} placeholder="Arrosage" value={form.watering} onChange={(e) => setForm({ ...form, watering: e.target.value })} data-testid="care-watering" />
                <input className={input} placeholder="Exposition soleil" value={form.sun_exposure} onChange={(e) => setForm({ ...form, sun_exposure: e.target.value })} data-testid="care-sun" />
                <input className={input} placeholder="Conseils d'entretien" value={form.care_tips} onChange={(e) => setForm({ ...form, care_tips: e.target.value })} />
                <input className={input} placeholder="Tailles disponibles (séparées par des virgules, ex: 8/10, 12/14)" value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="care-sizes" />
                <div className="rounded-lg bg-white border border-green-200 p-3 space-y-2">
                  <p className="text-xs font-semibold text-green-700">Engrais recommandé (calculateur de sacs)</p>
                  <input className={input} placeholder="Type d'engrais (ex: engrais organique arbres)" value={form.fertilizer_type} onChange={(e) => setForm({ ...form, fertilizer_type: e.target.value })} data-testid="fert-type" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" step="0.1" className={input} placeholder="Dose / plante (kg)" value={form.fertilizer_dose_kg} onChange={(e) => setForm({ ...form, fertilizer_dose_kg: e.target.value })} data-testid="fert-dose" />
                    <input type="number" step="0.1" className={input} placeholder="Contenance sac (kg)" value={form.fertilizer_bag_kg} onChange={(e) => setForm({ ...form, fertilizer_bag_kg: e.target.value })} data-testid="fert-bag" />
                  </div>
                </div>
                <input type="number" step="0.01" className={input} placeholder="Emprise au sol par unité (m²)" value={form.footprint_m2} onChange={(e) => setForm({ ...form, footprint_m2: e.target.value })} data-testid="veg-footprint" />
              </div>
            )}

            {/* Material fields */}
            {form.product_type === "materiau" && (
              <div className="rounded-lg border border-border bg-[#F0EFEA] p-4 space-y-3" data-testid="material-block">
                <p className="text-xs font-bold text-stone">Conditionnement</p>
                <select className={input} value={form.sold_by} onChange={(e) => setForm({ ...form, sold_by: e.target.value })} data-testid="mat-soldby">
                  <option value="unite">À l'unité</option>
                  <option value="bigbag">En big bag</option>
                  <option value="m3">Au m³ (vrac)</option>
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <input className={input} placeholder="Libellé unité (ex: big bag 1m³)" value={form.unit_label} onChange={(e) => setForm({ ...form, unit_label: e.target.value })} data-testid="mat-unit" />
                  {form.sold_by === "bigbag" && <input type="number" step="0.1" className={input} placeholder="Volume/big bag (m³)" value={form.bigbag_volume_m3} onChange={(e) => setForm({ ...form, bigbag_volume_m3: e.target.value })} data-testid="mat-volume" />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-stone font-medium">Densité (coefficient)</label>
                    <input type="number" step="0.05" className={input} placeholder="ex : 1.6" value={form.density} onChange={(e) => setForm({ ...form, density: e.target.value })} data-testid="mat-density" />
                    <p className="text-[10px] text-stone mt-0.5">Sable ~1,6 · gravier ~1,5 · terre ~1,3 · pouzzolane ~0,8 (1 m³ = densité × 1000 kg)</p>
                  </div>
                  <div>
                    <label className="text-xs text-stone font-medium">Emprise au sol / unité (m²)</label>
                    <input type="number" step="0.01" className={input} placeholder="ex : 0.25" value={form.footprint_m2} onChange={(e) => setForm({ ...form, footprint_m2: e.target.value })} data-testid="mat-footprint" />
                  </div>
                </div>
              </div>
            )}

            {/* Promo */}
            <div className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-4 space-y-3" data-testid="promo-block">
              <p className="text-xs font-bold text-terracotta flex items-center gap-1"><Tag size={13} /> Offre du moment (optionnel)</p>
              <div className="grid grid-cols-3 gap-2">
                <input type="number" step="0.01" className={input} placeholder="Prix promo €" value={form.promo_price} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} data-testid="promo-price" />
                <input type="date" className={input} value={form.promo_start} onChange={(e) => setForm({ ...form, promo_start: e.target.value })} data-testid="promo-start" />
                <input type="date" className={input} value={form.promo_end} onChange={(e) => setForm({ ...form, promo_end: e.target.value })} data-testid="promo-end" />
              </div>
            </div>

            {/* SKU + barcode */}
            {form.sku && (
              <div className="rounded-lg border border-border p-4 flex items-center justify-between" data-testid="barcode-block">
                <div><p className="text-xs text-stone">Référence</p><p className="font-mono font-semibold text-forest">{form.sku}</p><Barcode value={form.sku} /></div>
                <button type="button" onClick={() => printBarcode(form.sku, form.name)} data-testid="print-barcode" className="rounded-full border border-brand text-brand px-4 py-2 text-sm font-semibold hover:bg-brand/5 flex items-center gap-1.5"><Printer size={15} /> Imprimer</button>
              </div>
            )}

            {/* Admin-only margin block */}
            {isAdmin ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3" data-testid="margin-block">
                <p className="text-xs font-bold text-amber-700 flex items-center gap-1.5"><Lock size={13} /> Confidentiel — visible admin uniquement</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-stone">Prix d'achat (€)</label><input type="number" step="0.01" className={input} value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="prod-cost" /></div>
                  <div><label className="text-xs text-stone">Marge</label><div className="rounded-lg bg-white border border-border px-3 py-2 text-sm font-semibold text-green-700">{margin.toFixed(2)} € ({marginPct}%)</div></div>
                </div>
                <input className={input} placeholder="Fournisseur / origine" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="prod-supplier" />
                <div className="grid grid-cols-2 gap-3">
                  <input className={input} placeholder="Conditions (ex: franco 1000€)" value={form.purchase_conditions} onChange={(e) => setForm({ ...form, purchase_conditions: e.target.value })} />
                  <input className={input} placeholder="Délai de livraison" value={form.delivery_delay} onChange={(e) => setForm({ ...form, delivery_delay: e.target.value })} />
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-[#F0EFEA] p-3 text-xs text-stone flex items-center gap-2"><Lock size={13} /> Les données de marge (prix d'achat, fournisseur…) sont réservées à l'administrateur.</div>
            )}

            <button onClick={save} disabled={saving} data-testid="prod-save" className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />} {editing ? "Enregistrer" : "Ajouter le produit"}</button>
          </div>
        </div>

        {/* List */}
        <div className="rounded-xl bg-white border border-border overflow-hidden h-fit">
          {items.length === 0 ? <p className="text-stone text-sm p-8 text-center">Aucun produit.</p> : (
            <div className="divide-y divide-border">
              {items.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`prod-row-${p.id}`}>
                  <img src={fileUrl(p.image)} alt="" className="h-12 w-12 rounded-lg object-cover bg-sage" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-forest truncate">{p.name} {!p.active && <span className="text-[10px] bg-stone/20 rounded px-1.5 py-0.5 ml-1">Masqué</span>}</p>
                    <p className="text-xs text-stone">{p.category} · {Number(p.price).toFixed(2)} € · stock {p.stock}{isAdmin && p.cost_price ? ` · marge ${(p.price - p.cost_price).toFixed(2)}€` : ""}</p>
                  </div>
                  <button onClick={() => edit(p)} data-testid={`prod-edit-${p.id}`} className="text-stone hover:text-brand p-2"><Pencil size={16} /></button>
                  <button onClick={() => remove(p.id)} data-testid={`prod-delete-${p.id}`} className="text-red-500 hover:bg-red-50 rounded p-2"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showCatModal && <CategoryModal cats={cats} onClose={() => setShowCatModal(false)} onChanged={loadCats} />}
    </div>
  );
}

const catInput = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30";

function CategoryModal({ cats, onClose, onChanged }) {
  const [items, setItems] = useState(cats);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const refresh = async () => { const { data } = await api.get("/categories"); setItems(data); onChanged(); };

  const add = async () => {
    if (!newName.trim()) return;
    try { await api.post("/categories", { name: newName.trim() }); setNewName(""); toast.success("Catégorie ajoutée"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const saveEdit = async (id) => {
    try { await api.put(`/categories/${id}`, { name: editName.trim() }); setEditId(null); toast.success("Catégorie renommée (produits mis à jour)"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Catégorie supprimée"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="category-modal">
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold text-forest flex items-center gap-2"><FolderCog size={18} className="text-brand" /> Catégories de produits</h3>
          <button onClick={onClose} data-testid="category-modal-close" className="p-2 rounded-lg hover:bg-sage/50"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          <div className="flex gap-2">
            <input className={catInput} placeholder="Nouvelle catégorie" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="cat-new-input" />
            <button onClick={add} data-testid="cat-add" className="rounded-lg bg-brand text-cream px-4 text-sm font-semibold hover:bg-forest flex items-center gap-1 shrink-0"><Plus size={15} /> Ajouter</button>
          </div>
          <div className="divide-y divide-border border border-border rounded-xl">
            {items.map((c) => (
              <div key={c.id} className="flex items-center gap-2 px-3 py-2.5" data-testid={`cat-item-${c.id}`}>
                {editId === c.id ? (
                  <>
                    <input className={catInput} value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(c.id)} data-testid="cat-edit-input" autoFocus />
                    <button onClick={() => saveEdit(c.id)} data-testid="cat-save" className="text-green-600 p-1.5 hover:bg-green-50 rounded"><Check size={16} /></button>
                    <button onClick={() => setEditId(null)} className="text-stone p-1.5 hover:bg-sage/50 rounded"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-forest">{c.name}</span>
                    <button onClick={() => { setEditId(c.id); setEditName(c.name); }} data-testid={`cat-edit-${c.id}`} className="text-stone hover:text-brand p-1.5"><Pencil size={15} /></button>
                    <button onClick={() => del(c.id)} data-testid={`cat-delete-${c.id}`} className="text-red-500 hover:bg-red-50 rounded p-1.5"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-stone">Renommer une catégorie met à jour tous les produits associés. La suppression est bloquée si des produits l'utilisent encore.</p>
        </div>
      </div>
    </div>
  );
}

